import React, { useEffect, useRef, useState } from "react";
import { X, RefreshCw, AlertTriangle, CheckCircle2, CloudSun, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

/**
 * Portfolio-wide historic weather backfill dialog.
 *
 * Kicks off `POST /monitoring/portfolio/backfill-weather` (background task)
 * and polls `GET /monitoring/portfolio/weather-backfill-status` every 2 s
 * to render a live progress bar + per-site rollup. Refuses to start a
 * second concurrent run (server-side guard).
 */
export default function PortfolioWeatherBackfillDialog({ onClose }) {
  const [lookback, setLookback] = useState(365);
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/monitoring/portfolio/weather-backfill-status");
        setStatus(data);
        if (data.state === "finished" || data.state === "idle") {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch { /* transient — keep polling */ }
    }, 2000);
  };

  // On mount, resume any in-flight run
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/monitoring/portfolio/weather-backfill-status");
        if (data.state === "running") {
          setStatus(data);
          startPolling();
        }
      } catch { /* silent */ }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/monitoring/portfolio/backfill-weather", {
        lookback_days: lookback, only_with_coordinates: true,
      });
      if (data.status === "started") {
        toast.success(`Weather backfill started across ${data.total_sites} site(s)`);
        setStatus({ state: "running", total_sites: data.total_sites,
                    sites_done: 0, sites_ok: 0, sites_error: 0,
                    hours_written: 0, monitoring_rows_updated: 0, errors: [] });
        startPolling();
      } else if (data.status === "already_running") {
        toast.info("A weather backfill is already in progress");
        setStatus(data.progress);
        startPolling();
      } else if (data.status === "no_sites_with_coordinates") {
        toast.warning("No sites have latitude / longitude configured");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start weather backfill");
    } finally { setStarting(false); }
  };

  const pct = status && status.total_sites > 0
    ? Math.min(100, Math.round((status.sites_done / status.total_sites) * 100))
    : 0;
  const isRunning = status?.state === "running";
  const isFinished = status?.state === "finished";

  return (
    <div className="fixed inset-0 bg-black/45 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="portfolio-weather-backfill-dialog">
      <div className="bg-white w-full max-w-2xl border border-[var(--line)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="label flex items-center gap-2">
              <CloudSun size={11} /> Portfolio weather backfill · Open-Meteo GHI
            </div>
            <div className="font-display text-xl font-semibold mt-1">
              Fill historic weather across every site
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100"
                  data-testid="portfolio-weather-close-btn"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5">
          {!isRunning && !isFinished && (
            <>
              <p className="text-sm muted leading-relaxed">
                Iterates every site with a latitude &amp; longitude and pulls hourly Global Horizontal Irradiance from Open-Meteo. Historic monitoring rows in the range get re-scored with fresh estimated production &amp; variance. Idempotent — safe to re-run.
              </p>
              <div>
                <label className="block">
                  <span className="label mb-1 block">Lookback period</span>
                  <select className="input-swiss w-full font-mono text-xs"
                          value={lookback} onChange={(e) => setLookback(Number(e.target.value))}
                          data-testid="portfolio-weather-lookback-select">
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                    <option value={180}>180 days</option>
                    <option value={365}>1 year</option>
                    <option value={730}>2 years</option>
                    <option value={1095}>3 years</option>
                    <option value={1826}>5 years (max)</option>
                  </select>
                </label>
              </div>
              <div className="p-3 bg-[var(--surface-2)] border border-[var(--line)] text-[11px] muted flex items-start gap-2">
                <CloudSun size={13} className="mt-0.5 shrink-0" />
                <span>Open-Meteo is free — no API key or rate limit worth mentioning. A 5-year backfill across 20+ sites typically completes in under 2 minutes.</span>
              </div>
            </>
          )}

          {(isRunning || isFinished) && status && (
            <>
              <div className="flex items-center justify-between">
                <div className="label">Progress</div>
                <div className="font-mono text-xs">
                  {status.sites_done} / {status.total_sites} sites
                </div>
              </div>
              <div className="w-full h-3 bg-[var(--surface-2)] border border-[var(--line)] overflow-hidden">
                <div className="h-full transition-all"
                     style={{ width: `${pct}%`,
                              background: isFinished
                                ? (status.sites_error > 0 ? "var(--warn)" : "var(--ok)")
                                : "var(--accent)" }}
                     data-testid="portfolio-weather-progress-bar" />
              </div>

              {isRunning && status.current_site && (
                <div className="text-xs muted flex items-center gap-2">
                  <RefreshCw size={12} className="animate-spin" />
                  Processing: <span className="font-mono">{status.current_site.name}</span>
                </div>
              )}

              <div className="grid grid-cols-4 gap-2 mt-2">
                <StatCell label="OK"           value={status.sites_ok}                  tone="ok" testid="weather-stat-ok" />
                <StatCell label="Errors"       value={status.sites_error}               tone={status.sites_error > 0 ? "danger" : "muted"} testid="weather-stat-err" />
                <StatCell label="Hours"        value={(status.hours_written || 0).toLocaleString()} testid="weather-stat-hours" />
                <StatCell label="Rows re-scored" value={(status.monitoring_rows_updated || 0).toLocaleString()} testid="weather-stat-rescored" />
              </div>

              {status.errors && status.errors.length > 0 && (
                <div className="mt-3 p-3 border border-red-200 bg-red-50" data-testid="portfolio-weather-error-list">
                  <div className="label text-red-800 mb-2">Errors ({status.errors.length})</div>
                  <ul className="text-[10px] font-mono text-red-800 max-h-32 overflow-y-auto space-y-1">
                    {status.errors.slice(-15).map((e, i) => (
                      <li key={i}><strong>{e.project}</strong> — {typeof e.error === "string" ? e.error : JSON.stringify(e.error)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {isFinished && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-900 flex items-center gap-2"
                     data-testid="portfolio-weather-finished">
                  <CheckCircle2 size={14} />
                  <span className="text-sm">
                    Done. Wrote <strong>{(status.hours_written || 0).toLocaleString()}</strong> hourly records across <strong>{status.sites_ok}</strong> site(s), re-scoring <strong>{(status.monitoring_rows_updated || 0).toLocaleString()}</strong> monitoring rows.
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end items-center gap-2 p-5 border-t border-[var(--line)] bg-[var(--surface-2)]">
          <button onClick={onClose} className="btn-ghost"
                  data-testid="portfolio-weather-cancel-btn">
            {isRunning ? "Close (keeps running)" : "Close"}
          </button>
          {!isRunning && (
            <button onClick={start} disabled={starting}
                    className="btn-primary bg-[var(--accent)] border-[var(--accent)]"
                    data-testid="portfolio-weather-start-btn">
              <PlayCircle size={13} />
              {starting ? "Starting…" : isFinished ? "Run again" : "Start weather backfill"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, testid, tone = "muted" }) {
  const tones = {
    ok:     "bg-emerald-50 border-emerald-200 text-emerald-900",
    danger: "bg-red-50 border-red-200 text-red-800",
    muted:  "bg-white border-[var(--line)] text-[var(--ink)]",
  };
  return (
    <div className={`p-3 border ${tones[tone] || tones.muted}`} data-testid={testid}>
      <div className="label">{label}</div>
      <div className="font-display text-lg font-bold mt-0.5 font-mono">{value}</div>
    </div>
  );
}
